import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

/** A single entry in the dropdown. Structurally identical to FieldOption so the two interop. */
export interface SearchableSelectOption {
  label: string;
  value: string | number | boolean | null;
}

/**
 * How the control is wrapped.
 * - `form-field` — wrapped in a mat-form-field, the stock Material look used by page filters.
 * - `plain`      — a bare mat-select, for hosts that style mat-select themselves (cfi-record-edit).
 */
export type SearchableSelectVariant = 'form-field' | 'plain';

/**
 * A mat-select with a search box in its panel, usable anywhere a plain select is.
 *
 * The caller loads the full option list once; searching filters it in the browser, so opening and
 * typing cost no further requests.
 *
 * A ControlValueAccessor holding the option's value — not the option object — so it is a drop-in
 * for a mat-select. The selected option is pinned into the list even when a search would filter it
 * out, so the trigger never goes blank mid-search.
 */
@Component({
  selector: 'cfi-searchable-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, NgxMatSelectSearchModule],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})
export class SearchableSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  /** The full option list, loaded once by the caller. */
  @Input() options: SearchableSelectOption[] = [];

  /** Known label for the current value, so the trigger reads correctly before options arrive. */
  @Input() selectedOption: SearchableSelectOption | null = null;

  @Input() variant: SearchableSelectVariant = 'form-field';

  /** Extra class on the overlay panel, so a host can align it with its other selects. */
  @Input() panelClass = '';

  @Input() placeholder = '';
  @Input() searchPlaceholder = 'Search';
  @Input() noEntriesLabel = 'No entries found';

  /** DOM id of the mat-select, so an external label[for] targets it. */
  @Input() controlId = '';
  /** Prefix for the data-pw / data-qa hooks, e.g. 'rosters-school'. */
  @Input() testPrefix = '';
  /** Joins `testPrefix` to the hook suffix, so hosts can match their own naming. */
  @Input() testSeparator = '-';
  @Input() ariaLabelledBy: string | null = null;
  @Input() ariaDescribedBy: string | null = null;

  /** Emits the selected value. The bound form control is updated regardless. */
  @Output() selectionChange = new EventEmitter<SearchableSelectOption['value']>();

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  value: SearchableSelectOption['value'] = null;
  isDisabled = false;

  /**
   * What the panel renders. Recomputed on search/option changes rather than exposed as a getter, so
   * change detection does not re-filter the whole list every pass.
   */
  visibleOptions: SearchableSelectOption[] = [];

  private query = '';
  private pinned: SearchableSelectOption | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  private onChange: (value: SearchableSelectOption['value']) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((query) => {
      this.query = (query ?? '').trim().toLowerCase();
      this.refreshVisibleOptions();
      this.cdr.markForCheck();
    });
    this.resolvePinned();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['selectedOption']) {
      this.resolvePinned();
    }
  }

  onSelectionChange(value: SearchableSelectOption['value']): void {
    this.value = value;
    this.pinned = value == null ? null : this.findOption(value) ?? this.pinned;
    this.refreshVisibleOptions();
    this.onChange(value);
    this.selectionChange.emit(value);
  }

  writeValue(value: SearchableSelectOption['value']): void {
    this.value = value ?? null;
    this.resolvePinned();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: SearchableSelectOption['value']) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  markTouched(): void {
    this.onTouched();
  }

  trackByValue(_: number, option: SearchableSelectOption): string {
    return String(option.value);
  }

  /** Keeps `pinned` pointing at the option matching the current value. */
  private resolvePinned(): void {
    if (this.value == null) {
      this.pinned = null;
    } else {
      this.pinned =
        this.findOption(this.value) ??
        (this.selectedOption?.value === this.value ? this.selectedOption : this.pinned);
    }
    this.refreshVisibleOptions();
  }

  private refreshVisibleOptions(): void {
    const all = this.options ?? [];
    const list = this.query
      ? all.filter((option) => (option.label ?? '').toLowerCase().includes(this.query))
      : all;

    this.visibleOptions =
      !this.pinned || list.some((option) => option.value === this.pinned?.value) ? list : [this.pinned, ...list];
  }

  private findOption(value: SearchableSelectOption['value']): SearchableSelectOption | null {
    return (this.options ?? []).find((option) => option.value === value) ?? null;
  }
}
